import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { DashboardService } from '../utils/dashboardService';
import { Event } from '../utils/types';
import { apiGet } from '../utils/api';

const JoinEvent: React.FC = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId?: string }>();
  const { token, user } = useAuth();
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const loadCurrentEvent = async () => {
      if (!token) return;

      setLoading(true);
      setError('');

      try {
        let event: Event;
        
        if (eventId) {
          // Load specific event if provided
          event = await apiGet<Event>(`/events/${eventId}`, token);
        } else {
          // Load current active event
          const currentEventData = await DashboardService.getCurrentEvent(token);
          event = currentEventData as Event;
        }
        
        setCurrentEvent(event);
      } catch (err: any) {
        console.error('Failed to load event:', err);
        setError(err.message || 'Failed to load event information');
      } finally {
        setLoading(false);
      }
    };

    loadCurrentEvent();
  }, [token, eventId]);

  const handleCreateTeam = () => {
    if (currentEvent) {
      // Navigate to create team page with event pre-selected
      navigate(`/create-team?eventId=${currentEvent.event_id}`);
    } else {
      navigate('/create-team');
    }
  };

  const handleJoinTeam = () => {
    if (currentEvent) {
      // Navigate to teams list filtered for teams looking for members
      navigate(`/teams?eventId=${currentEvent.event_id}`);
    } else {
      navigate('/teams?filter=looking');
    }
  };

  const handleFindTeam = () => {
    if (currentEvent) {
      // Navigate to event registration page
      navigate(`/events/${currentEvent.event_id}/register`);
    } else {
      navigate('/events');
    }
  };

  if (loading) {
    return (
      <div>
        <div style={{ marginBottom: '20px' }}>
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            ← Back to Home
          </button>
        </div>
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading event information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div style={{ marginBottom: '20px' }}>
          <button onClick={() => navigate('/')} className="btn btn-secondary">
            ← Back to Home
          </button>
        </div>
        <div className="error-container">
          <h2>❌ Error Loading Event</h2>
          <p>{error}</p>
          <button onClick={() => window.location.reload()} className="btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <button onClick={() => navigate('/')} className="btn btn-secondary">
          ← Back to Home
        </button>
      </div>

      <div className="card">
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '20px',
          maxWidth: '600px',
          margin: '0 auto'
        }}>
          {/* Create Team Option */}
          <button
            onClick={handleCreateTeam}
            className="action-card"
            style={{
              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
              color: 'white',
              boxShadow: '0 4px 8px rgba(40, 167, 69, 0.3)',
              border: 'none',
              padding: '25px',
              borderRadius: '12px',
              width: '100%'
            }}
          >
            <div className="action-icon" style={{ fontSize: '32px' }}>
              🏗️
            </div>
            <div className="action-content" style={{ textAlign: 'left' }}>
              <strong style={{ fontSize: '20px', display: 'block', marginBottom: '8px' }}>
                I want to create a team
              </strong>
              <small style={{ fontSize: '16px', opacity: 0.9 }}>
                Click here if you are leading a team and want to create it...
              </small>
            </div>
          </button>

          {/* Join Team Option */}
          <button
            onClick={handleJoinTeam}
            className="action-card"
            style={{
              background: 'linear-gradient(135deg, #007bff 0%, #6610f2 100%)',
              color: 'white',
              boxShadow: '0 4px 8px rgba(0, 123, 255, 0.3)',
              border: 'none',
              padding: '25px',
              borderRadius: '12px',
              width: '100%'
            }}
          >
            <div className="action-icon" style={{ fontSize: '32px' }}>
              👥
            </div>
            <div className="action-content" style={{ textAlign: 'left' }}>
              <strong style={{ fontSize: '20px', display: 'block', marginBottom: '8px' }}>
                I want to join my team
              </strong>
              <small style={{ fontSize: '16px', opacity: 0.9 }}>
                Click here if you have a team and want to join it...
              </small>
            </div>
          </button>

          {/* Find Team Option */}
          <button
            onClick={handleFindTeam}
            className="action-card"
            style={{
              background: 'linear-gradient(135deg, #fd7e14 0%, #e83e8c 100%)',
              color: 'white',
              boxShadow: '0 4px 8px rgba(253, 126, 20, 0.3)',
              border: 'none',
              padding: '25px',
              borderRadius: '12px',
              width: '100%'
            }}
          >
            <div className="action-icon" style={{ fontSize: '32px' }}>
              🔍
            </div>
            <div className="action-content" style={{ textAlign: 'left' }}>
              <strong style={{ fontSize: '20px', display: 'block', marginBottom: '8px' }}>
                I don't have a team
              </strong>
              <small style={{ fontSize: '16px', opacity: 0.9 }}>
                Click here if you don't have a team but want to find one...
              </small>
            </div>
          </button>
        </div>
        <div style={{ textAlign: 'center', marginBottom: '40px' }}>
          
          {currentEvent && (
            <div style={{ 
              background: '#f8f9fa', 
              padding: '15px', 
              borderRadius: '8px',
          marginTop: '40px',
              border: '1px solid #dee2e6'
            }}>
              <h3 style={{ margin: '0 0 5px 0', color: '#333' }}>
                Current Event: {currentEvent.name}
              </h3>
              <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                {currentEvent.description}
              </p>
              <div style={{ marginTop: '10px' }}>
                <span className={`badge badge-${currentEvent.state}`}>
                  {currentEvent.state}
                </span>
              </div>
            </div>
          )}
        </div>

        <div style={{ 
          textAlign: 'center', 
          marginTop: '40px',
          padding: '20px',
          background: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <p style={{ 
            color: '#666', 
            fontSize: '14px', 
            margin: '0 0 15px 0',
            fontStyle: 'italic'
          }}>
            💡 Need help deciding? Each option will guide you through the process!
          </p>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <button 
              onClick={() => navigate('/teams')} 
              className="btn btn-secondary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              View All Teams
            </button>
            <button 
              onClick={() => navigate('/events')} 
              className="btn btn-secondary"
              style={{ fontSize: '14px', padding: '8px 16px' }}
            >
              View All Events
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinEvent;
